<template>
  <component
    :is="interactive ? 'button' : 'span'"
    :type="interactive ? 'button' : null"
    class="ksk-chip"
    :class="[`ksk-chip--${variant}`, { 'ksk-chip--selected': selected, 'ksk-chip--interactive': interactive }]"
    v-bind="colorSlot ? kskSlot(colorSlot) : {}"
    @click="interactive && $emit('click', $event)"
  >
    <span v-if="icon" class="material-icons-round ksk-chip__icon">{{ icon }}</span>
    <slot />
  </component>
</template>

<script>
import { kskSlot } from "@/config/kioskColorSlots";

// KSK004 — chips / pills / badges.
//   pill  : 44px header utility pill (Dine In, Sign In …)
//   chip  : 56px selectable filter chip
//   badge : small non-interactive status label
export default {
  name: "KskChip",
  emits: ["click"],
  props: {
    variant: {
      type: String,
      default: "pill",
      validator: (v) => ["pill", "chip", "badge", "badge-warning", "badge-error"].includes(v),
    },
    icon: { type: String, default: "" },
    selected: { type: Boolean, default: false },
    interactive: { type: Boolean, default: false },
    // KSK063: optional colour slot (kioskColorSlots.js) for pill / chip.
    colorSlot: { type: String, default: "" },
  },
  methods: { kskSlot },
};
</script>

<style scoped>
.ksk-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--ksk-space-1);
  /* KSK063: --ksk-slot-* is only set when the chip has a colour slot. */
  border: 1px solid var(--ksk-slot-outline, var(--ksk-border));
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-slot-accent, var(--ksk-surface));
  color: var(--ksk-slot-font, var(--ksk-text));
  font-family: var(--ksk-font);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.ksk-chip--pill {
  height: 44px;
  padding: 0 var(--ksk-space-2);
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
}
.ksk-chip--chip {
  height: 56px;
  padding: 0 var(--ksk-space-3);
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
}
.ksk-chip--badge,
.ksk-chip--badge-warning,
.ksk-chip--badge-error {
  height: 32px;
  padding: 0 12px;
  border: none;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-chip--badge-warning {
  background: var(--ksk-warning);
  color: var(--ksk-on-status);
}
.ksk-chip--badge-error {
  background: var(--ksk-error);
  color: var(--ksk-on-status);
}

.ksk-chip--selected {
  background: var(--ksk-slot-accent, var(--ksk-primary));
  border-color: var(--ksk-slot-outline, var(--ksk-primary));
  color: var(--ksk-slot-font, var(--ksk-on-primary));
}

.ksk-chip__icon {
  font-size: 24px;
  line-height: 1;
  color: var(--ksk-slot-font, var(--ksk-primary));
}
.ksk-chip--selected .ksk-chip__icon,
.ksk-chip--badge-warning .ksk-chip__icon,
.ksk-chip--badge-error .ksk-chip__icon {
  color: inherit;
}

.ksk-chip--interactive {
  cursor: pointer;
  transition: transform 80ms ease-out;
}
.ksk-chip--interactive::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
}
.ksk-chip--interactive:active {
  transform: scale(0.97);
}
.ksk-chip--interactive:active::after {
  opacity: 0.08;
}
</style>
